import type { WarehouseLedgerTransactionPatch } from '@/services/warehouseTypes'
import type { EditableLedgerRow } from '@/components/WarehouseLedgerTable'

export type LedgerEditableSnapshot = {
  txn_date: string
  bk_sku: string
  kind: string
  signed_cases: number
  store: string | null
  unit_cost: number | null
  product_name: string | null
  order_number: string | null
  username: string | null
  notes: string | null
  shipping_alloc: number
  pick_fee_alloc: number
  receiving_alloc: number
}

export function snapshotLedgerEditableFields(row: LedgerEditableSnapshot): LedgerEditableSnapshot {
  return {
    txn_date: row.txn_date,
    bk_sku: row.bk_sku,
    kind: row.kind,
    signed_cases: row.signed_cases,
    store: row.store,
    unit_cost: row.unit_cost,
    product_name: row.product_name,
    order_number: row.order_number,
    username: row.username,
    notes: row.notes ?? '',
    shipping_alloc: row.shipping_alloc,
    pick_fee_alloc: row.pick_fee_alloc,
    receiving_alloc: row.receiving_alloc,
  }
}

function ledgerEditableFieldsEqual(a: LedgerEditableSnapshot, b: LedgerEditableSnapshot): boolean {
  return (
    a.txn_date === b.txn_date &&
    a.bk_sku === b.bk_sku &&
    a.kind === b.kind &&
    a.signed_cases === b.signed_cases &&
    (a.store ?? '') === (b.store ?? '') &&
    a.unit_cost === b.unit_cost &&
    (a.product_name ?? '') === (b.product_name ?? '') &&
    (a.order_number ?? '') === (b.order_number ?? '') &&
    (a.username ?? '') === (b.username ?? '') &&
    (a.notes ?? '') === (b.notes ?? '') &&
    a.shipping_alloc === b.shipping_alloc &&
    a.pick_fee_alloc === b.pick_fee_alloc &&
    a.receiving_alloc === b.receiving_alloc
  )
}

export function isLedgerRowDirtyAgainstBaseline(
  row: LedgerEditableSnapshot,
  baseline: LedgerEditableSnapshot | undefined
): boolean {
  if (!baseline) return true
  return !ledgerEditableFieldsEqual(row, baseline)
}

export function isSaveableDirtyLedgerRow(row: EditableLedgerRow): boolean {
  return row.dirty && row.bk_sku.trim() !== ''
}

export function ledgerRowToPatch(
  row: LedgerEditableSnapshot,
  baseline: LedgerEditableSnapshot
): WarehouseLedgerTransactionPatch {
  const patch: WarehouseLedgerTransactionPatch = {}
  if (row.txn_date !== baseline.txn_date) patch.txn_date = row.txn_date
  if (row.bk_sku !== baseline.bk_sku) patch.bk_sku = row.bk_sku
  if (row.kind !== baseline.kind) patch.kind = row.kind as WarehouseLedgerTransactionPatch['kind']
  if (row.signed_cases !== baseline.signed_cases) patch.signed_cases = row.signed_cases
  if ((row.store ?? '') !== (baseline.store ?? '')) patch.store = row.store
  if (row.unit_cost !== baseline.unit_cost) patch.unit_cost = row.unit_cost
  if ((row.product_name ?? '') !== (baseline.product_name ?? '')) {
    patch.product_name = row.product_name
  }
  if ((row.order_number ?? '') !== (baseline.order_number ?? '')) {
    patch.order_number = row.order_number
  }
  if ((row.username ?? '') !== (baseline.username ?? '')) patch.username = row.username
  if ((row.notes ?? '') !== (baseline.notes ?? '')) patch.notes = row.notes
  if (row.shipping_alloc !== baseline.shipping_alloc) {
    patch.shipping_alloc = row.shipping_alloc
  }
  if (row.pick_fee_alloc !== baseline.pick_fee_alloc) {
    patch.pick_fee_alloc = row.pick_fee_alloc
  }
  if (row.receiving_alloc !== baseline.receiving_alloc) {
    patch.receiving_alloc = row.receiving_alloc
  }
  return patch
}
